import { ChannelType, ChannelTypeStrings, SimpleTypes } from 'icreate/common/models/channel-type';
import { CHANNEL_RANGE_VALIDATION } from 'icreate/common/models/channel-validate';
import { ExcelParsedProperties as props } from 'icreate/common/models/channel-config';
import * as _ from 'lodash';
import { getChannelStatusString, getSimpleTypeChannelSheet } from 'icreate/common/services/channel-object-utils';

export function generateExcelRow(obj: any) {
    let { fcuData, opsData } = obj;
    let excelNewMap: Map<any, any> = new Map(
        Array.from(ExcelKeysMap.keys(), key => {
            const range = CHANNEL_RANGE_VALIDATION[props[key]];
            const defaultValue = range ? `${range[0]}` : '';
            return [key, defaultValue];
        })
    );
    let alarmLevel = ['Caution', 'Warning', 'Alarm'];
    const scale = fcuData?.scale || 1;
    const scaleDecimals = scale === 1 ? 0 : Math.round(Math.log10(scale));

    if (fcuData.channelType == 100) {
        for (const key of excelNewMap.keys()) {
            const column = excelNewMap.get(key) as string;
            if (column.includes('enable') || column.includes('normal open flag')) {
                excelNewMap.set(key, false);
            } else {
                excelNewMap.set(key, '');
            }
        }
        excelNewMap.set(camelToPascal('id'), obj['rowId']);
        excelNewMap.set('ChannelType', ChannelTypeStrings[ChannelType.BLANK]);
        excelNewMap.set('ChannelTypeSimple', SimpleTypes.Blank);
        excelNewMap.delete('Scale');
        const status = getChannelStatusString(obj);
        excelNewMap.set('Status', status);

        return Array.from(excelNewMap.values());
    }

    const nestedObjectToObject = (rowObject: any, parentKey = '') => {
        for (const key in rowObject) {
            const newKey = parentKey ? `${parentKey}${camelToPascal(key)}` : key;
            if (typeof rowObject[key] === 'object' && rowObject[key] !== null) {
                nestedObjectToObject(rowObject[key], newKey);
            } else {
                let excelMapKey = camelToPascal(newKey);
                if (ExcelKeysMap.has(excelMapKey)) {
                    if (rowObject[key] == null) {
                        excelNewMap.set(excelMapKey, '');
                    } else if (newKey.includes('Range')) {
                        const num = Number(rowObject[key]);
                        if (Number.isFinite(num)) {
                            const str = num.toString();
                            const dot = str.indexOf('.');
                            const valueDecimals = dot === -1 ? 0 : str.length - dot - 1;
                            const decimals = Math.max(scaleDecimals, valueDecimals);
                            excelNewMap.set(excelMapKey, decimals > 0 ? num.toFixed(decimals) : String(num));
                        } else excelNewMap.set(excelMapKey, '');
                    } else if (typeof rowObject[key] === 'boolean') {
                        excelNewMap.set(excelMapKey, rowObject[key] == true);
                    } else if (newKey.includes('Level')) {
                        const level = alarmLevel[rowObject[key]];
                        excelNewMap.set(excelMapKey, level !== undefined ? level.toString() : '');
                    } else {
                        excelNewMap.set(excelMapKey, rowObject[key].toString());
                    }
                }
            }
        }
    };

    const fillExcelRow = (dataValues: any) => {
        for (const key in dataValues) {
            if (typeof dataValues[key] === 'object') {
                nestedObjectToObject(dataValues[key], key);
            } else {
                let excelMapKey = camelToPascal(key);
                if (ExcelKeysMap.has(excelMapKey) && excelMapKey !== 'Id') {
                    excelNewMap.set(excelMapKey, dataValues[key].toString());
                }
            }
        }
    };

    excelNewMap.set('rowId', obj['rowId']);

    fillExcelRow(fcuData);
    fillExcelRow(opsData);
    excelNewMap.set('ChannelTypeSimple', getSimpleTypeChannelSheet(fcuData));
    const status = getChannelStatusString(obj);
    excelNewMap.set('Status', status);

    return Array.from(excelNewMap.values());
}

function camelToPascal(camelKey: string) {
    return camelKey.charAt(0).toUpperCase() + camelKey.substring(1);
}

export function parseExcelRow(excelRow: any) {
    const alarmLevel = ['Caution', 'Warning', 'Alarm'];

    const excelProperty = Object.values(ExcelParsedProperties);

    const test: any = {};
    let maxMeasurableRangeDecimals = 0;
    let maxNormalRangeDecimals = 0;
    let maxSetpointDecimals = 0;

    excelRow.forEach((item, index) => {
        const key = excelProperty[index];
        if (!key) return;
        if (key.includes('Range')) {
            if (key.includes('measurableRange')) {
                const str = String(item ?? '');
                const dot = str.indexOf('.');
                const decimals = dot === -1 ? 0 : str.length - dot - 1;
                if (decimals > maxMeasurableRangeDecimals) {
                    maxMeasurableRangeDecimals = decimals;
                }
            } else if (key.includes('normalRange')) {
                const str = String(item ?? '');
                const dot = str.indexOf('.');
                const decimals = dot === -1 ? 0 : str.length - dot - 1;
                if (decimals > maxNormalRangeDecimals) {
                    maxNormalRangeDecimals = decimals;
                }
            }
            test[key] = parseFloat(item) || 0;
        } else if (key.includes('setPoint')) {
            const str = String(item ?? '');
            const dot = str.indexOf('.');
            const decimals = dot === -1 ? 0 : str.length - dot - 1;
            if (decimals > maxSetpointDecimals) {
                maxSetpointDecimals = decimals;
            }
            test[key] = str ? parseFloat(item) || 0 : null;
        } else if (key.includes('enable')) {
            if (item === 'true' || item === 'false') {
                test[key] = item === 'true';
            } else test[key] = item === true;
        } else if (alarmLevel.includes(item)) {
            test[key] = alarmLevel.indexOf(item);
        } else if (item && !isNaN(item) && !key.includes('tag') && !key.includes('rowId')) {
            test[key] = Number(item);
        } else {
            test[key] = item;
        }
    });

    const formated: any = transformObject(test);

    if (formated?.fcuData && maxMeasurableRangeDecimals > 0) {
        const inferredScale = Math.pow(
            10,
            Math.max(maxNormalRangeDecimals, maxSetpointDecimals, maxMeasurableRangeDecimals)
        );

        if (!formated.fcuData.scale || formated.fcuData.scale <= 1) {
            formated.fcuData.scale = inferredScale;
        }
    }

    const digitalAlarmSetting = formated?.fcuData?.digitalAlarmSetting;
    if (digitalAlarmSetting && !digitalAlarmSetting.statusString) {
        const on = digitalAlarmSetting.statusStringContactOn;
        const off = digitalAlarmSetting.statusStringContactOff;
        if (on || off) {
            digitalAlarmSetting.statusString = `${on ?? ''}/${off ?? ''}`;
        }
    }

    function transformObject(input) {
        const result = {};

        _.forEach(input, (value, key) => {
            const keys = key.split('.');
            _.set(result, keys, value);
        });

        return result;
    }

    const channelTypeString = formated?.opsData?.channelType;
    if (formated?.fcuData && channelTypeString) {
        const channelType = Object.keys(ChannelTypeStrings).find(type => ChannelTypeStrings[type] == channelTypeString);
        if (channelType) formated.fcuData.channelType = Number(channelType);
    }

    formated[ExcelParsedProperties.Status] = getChannelStatusString(formated);

    return formated;
}

const ExcelParsedProperties = {
    rowId: 'rowId',
    FcuNo: 'fcuData.fcuNo',
    ChannelType: 'opsData.channelType',
    ChannelTypeSimple: '',
    Tag: 'opsData.tag',
    Name: 'opsData.name',
    Status: 'status',
    MeasurableRangeMin: 'fcuData.measurableRange.min',
    MeasurableRangeMax: 'fcuData.measurableRange.max',
    NormalRangeMin: 'fcuData.normalRange.min',
    NormalRangeMax: 'fcuData.normalRange.max',
    MeasurementUnit: 'opsData.measurementUnit',
    Scale: 'fcuData.scale',
    FixedDigit: 'fcuData.fixedDigit',

    InputDataFuInputAddressFuNo: 'fcuData.inputData.fuInputAddress.fuNo',
    InputDataFuInputAddressSlotNo: 'fcuData.inputData.fuInputAddress.slotNo',
    InputDataFuInputAddressPin: 'fcuData.inputData.fuInputAddress.pin',
    InputDataFuInputAddressPinCount: 'fcuData.inputData.fuInputAddress.pinCount',
    InputDataMemoryInputAddressAddress: 'fcuData.inputData.memoryInputAddress.address',
    InputDataMemoryInputAddressMask: 'fcuData.inputData.memoryInputAddress.mask',
    InputDataMiscDigitalInputFilter: 'fcuData.inputData.misc.digitalInputFilter',
    InputDataMiscHysteresis: 'fcuData.inputData.misc.hysteresis',

    OutputDataFuOutputAddressFuNo: 'fcuData.outputData.fuOutputAddress.fuNo',
    OutputDataFuOutputAddressSlotNo: 'fcuData.outputData.fuOutputAddress.slotNo',
    OutputDataFuOutputAddressPin: 'fcuData.outputData.fuOutputAddress.pin',
    OutputDataFuOutputAddressPinCount: 'fcuData.outputData.fuOutputAddress.pinCount',
    OutputDataMemoryOutputAddressAddress: 'fcuData.outputData.memoryOutputAddress.address',
    OutputDataMemoryOutputAddressMask: 'fcuData.outputData.memoryOutputAddress.mask',
    OutputDataMiscSetPoint: 'fcuData.outputData.misc.setPoint',
    OutputDataMiscSignalType: 'fcuData.outputData.misc.signalType',
    OutputDataMiscSignalWidth: 'fcuData.outputData.misc.signalWidth',
    OutputDataMiscHysteresisForOpen: 'fcuData.outputData.misc.hysteresisForOpen',
    OutputDataMiscHysteresisForClose: 'fcuData.outputData.misc.hysteresisForClose',

    DigitalAlarmSettingEnable: 'fcuData.digitalAlarmSetting.enable',
    DigitalAlarmSettingStatusStringContactOn: 'fcuData.digitalAlarmSetting.statusStringContactOn',
    DigitalAlarmSettingStatusStringContactOff: 'fcuData.digitalAlarmSetting.statusStringContactOff',
    DigitalAlarmSettingAlarmGroupNo: 'fcuData.digitalAlarmSetting.alarmGroupNo',
    DigitalAlarmSettingReposeGroup1No: 'fcuData.digitalAlarmSetting.reposeGroup1No',
    DigitalAlarmSettingReposeGroup2No: 'fcuData.digitalAlarmSetting.reposeGroup2No',
    DigitalAlarmSettingDelayTimer: 'fcuData.digitalAlarmSetting.delayTimer',
    DigitalAlarmSettingNormalOpenFlag: 'fcuData.digitalAlarmSetting.normalOpenFlag',
    DigitalAlarmSettingAlarmLevel: 'fcuData.digitalAlarmSetting.alarmLevel',

    MotorChannelSettingEnable: 'fcuData.motorChannelSetting.enable',
    MotorChannelSettingMotorType: 'fcuData.motorChannelSetting.motorType',
    MotorChannelSettingStopStatusString: 'fcuData.motorChannelSetting.stopStatusString',
    MotorChannelSettingStbyStatusString: 'fcuData.motorChannelSetting.stbyStatusString',
    MotorChannelSettingRunStatusString1: 'fcuData.motorChannelSetting.runStatusString1',
    MotorChannelSettingRunStatusString2: 'fcuData.motorChannelSetting.runStatusString2',
    MotorChannelSettingRunStatusString3: 'fcuData.motorChannelSetting.runStatusString3',
    MotorChannelSettingRunStatusString4: 'fcuData.motorChannelSetting.runStatusString4',
    MotorChannelSettingRunStatusString5: 'fcuData.motorChannelSetting.runStatusString5',
    MotorChannelSettingAlarmGroupNo: 'fcuData.motorChannelSetting.alarmGroupNo',
    MotorChannelSettingReposeGroup1No: 'fcuData.motorChannelSetting.reposeGroup1No',
    MotorChannelSettingReposeGroup2No: 'fcuData.motorChannelSetting.reposeGroup2No',
    MotorChannelSettingDelayTimer: 'fcuData.motorChannelSetting.delayTimer',
    MotorChannelSettingAlarmLevel: 'fcuData.motorChannelSetting.alarmLevel',

    AlarmSettingVeryHighEnable: 'fcuData.alarmSettingVeryHigh.enable',
    AlarmSettingVeryHighStatusString: 'fcuData.alarmSettingVeryHigh.statusString',
    AlarmSettingVeryHighSetPoint: 'fcuData.alarmSettingVeryHigh.setPoint',
    AlarmSettingVeryHighAlarmGroupNo: 'fcuData.alarmSettingVeryHigh.alarmGroupNo',
    AlarmSettingVeryHighReposeGroup1No: 'fcuData.alarmSettingVeryHigh.reposeGroup1No',
    AlarmSettingVeryHighReposeGroup2No: 'fcuData.alarmSettingVeryHigh.reposeGroup2No',
    AlarmSettingVeryHighDelayTimer: 'fcuData.alarmSettingVeryHigh.delayTimer',
    AlarmSettingVeryHighAlarmLevel: 'fcuData.alarmSettingVeryHigh.alarmLevel',

    AlarmSettingHighEnable: 'fcuData.alarmSettingHigh.enable',
    AlarmSettingHighStatusString: 'fcuData.alarmSettingHigh.statusString',
    AlarmSettingHighSetPoint: 'fcuData.alarmSettingHigh.setPoint',
    AlarmSettingHighAlarmGroupNo: 'fcuData.alarmSettingHigh.alarmGroupNo',
    AlarmSettingHighReposeGroup1No: 'fcuData.alarmSettingHigh.reposeGroup1No',
    AlarmSettingHighReposeGroup2No: 'fcuData.alarmSettingHigh.reposeGroup2No',
    AlarmSettingHighDelayTimer: 'fcuData.alarmSettingHigh.delayTimer',
    AlarmSettingHighAlarmLevel: 'fcuData.alarmSettingHigh.alarmLevel',

    AlarmSettingLowEnable: 'fcuData.alarmSettingLow.enable',
    AlarmSettingLowStatusString: 'fcuData.alarmSettingLow.statusString',
    AlarmSettingLowSetPoint: 'fcuData.alarmSettingLow.setPoint',
    AlarmSettingLowAlarmGroupNo: 'fcuData.alarmSettingLow.alarmGroupNo',
    AlarmSettingLowReposeGroup1No: 'fcuData.alarmSettingLow.reposeGroup1No',
    AlarmSettingLowReposeGroup2No: 'fcuData.alarmSettingLow.reposeGroup2No',
    AlarmSettingLowDelayTimer: 'fcuData.alarmSettingLow.delayTimer',
    AlarmSettingLowAlarmLevel: 'fcuData.alarmSettingLow.alarmLevel',

    AlarmSettingVeryLowEnable: 'fcuData.alarmSettingVeryLow.enable',
    AlarmSettingVeryLowStatusString: 'fcuData.alarmSettingVeryLow.statusString',
    AlarmSettingVeryLowSetPoint: 'fcuData.alarmSettingVeryLow.setPoint',
    AlarmSettingVeryLowAlarmGroupNo: 'fcuData.alarmSettingVeryLow.alarmGroupNo',
    AlarmSettingVeryLowReposeGroup1No: 'fcuData.alarmSettingVeryLow.reposeGroup1No',
    AlarmSettingVeryLowReposeGroup2No: 'fcuData.alarmSettingVeryLow.reposeGroup2No',
    AlarmSettingVeryLowDelayTimer: 'fcuData.alarmSettingVeryLow.delayTimer',
    AlarmSettingVeryLowAlarmLevel: 'fcuData.alarmSettingVeryLow.alarmLevel',

    AlarmSettingSensorFailEnable: 'fcuData.alarmSettingSensorFail.enable',
    AlarmSettingSensorFailStatusString: 'fcuData.alarmSettingSensorFail.statusString',
    AlarmSettingSensorFailAlarmGroupNo: 'fcuData.alarmSettingSensorFail.alarmGroupNo',
    AlarmSettingSensorFailReposeGroup1No: 'fcuData.alarmSettingSensorFail.reposeGroup1No',
    AlarmSettingSensorFailReposeGroup2No: 'fcuData.alarmSettingSensorFail.reposeGroup2No',
    AlarmSettingSensorFailDelayTimer: 'fcuData.alarmSettingSensorFail.delayTimer',
    AlarmSettingSensorFailAlarmLevel: 'fcuData.alarmSettingSensorFail.alarmLevel',

    AlarmSettingFeedbackErrorEnable: 'fcuData.alarmSettingFeedbackError.enable',
    AlarmSettingFeedbackErrorStatusString: 'fcuData.alarmSettingFeedbackError.statusString',
    AlarmSettingFeedbackErrorSetPoint: 'fcuData.alarmSettingFeedbackError.setPoint',
    AlarmSettingFeedbackErrorAlarmGroupNo: 'fcuData.alarmSettingFeedbackError.alarmGroupNo',
    AlarmSettingFeedbackErrorReposeGroup1No: 'fcuData.alarmSettingFeedbackError.reposeGroup1No',
    AlarmSettingFeedbackErrorReposeGroup2No: 'fcuData.alarmSettingFeedbackError.reposeGroup2No',
    AlarmSettingFeedbackErrorDelayTimer: 'fcuData.alarmSettingFeedbackError.delayTimer',
    AlarmSettingFeedbackErrorAlarmLevel: 'fcuData.alarmSettingFeedbackError.alarmLevel',

    SystemAlarmSettingEnable: 'fcuData.systemAlarmSetting.enable',
    SystemAlarmSettingStatusString1: 'fcuData.systemAlarmSetting.statusString1',
    SystemAlarmSettingStatusString2: 'fcuData.systemAlarmSetting.statusString2',
    SystemAlarmSettingStatusString3: 'fcuData.systemAlarmSetting.statusString3',
    SystemAlarmSettingStatusString4: 'fcuData.systemAlarmSetting.statusString4',
    SystemAlarmSettingStatusString5: 'fcuData.systemAlarmSetting.statusString5',
    SystemAlarmSettingStatusString6: 'fcuData.systemAlarmSetting.statusString6',
    SystemAlarmSettingStatusString7: 'fcuData.systemAlarmSetting.statusString7',
    SystemAlarmSettingStatusString8: 'fcuData.systemAlarmSetting.statusString8',
    SystemAlarmSettingStatusString9: 'fcuData.systemAlarmSetting.statusString9',
    SystemAlarmSettingStatusString10: 'fcuData.systemAlarmSetting.statusString10',
    SystemAlarmSettingStatusString11: 'fcuData.systemAlarmSetting.statusString11',
    SystemAlarmSettingStatusString12: 'fcuData.systemAlarmSetting.statusString12',
    SystemAlarmSettingStatusString13: 'fcuData.systemAlarmSetting.statusString13',
    SystemAlarmSettingStatusString14: 'fcuData.systemAlarmSetting.statusString14',
    SystemAlarmSettingStatusString15: 'fcuData.systemAlarmSetting.statusString15',
    SystemAlarmSettingStatusString16: 'fcuData.systemAlarmSetting.statusString16',
    SystemAlarmSettingAlarmGroupNo: 'fcuData.systemAlarmSetting.alarmGroupNo',
    SystemAlarmSettingReposeGroup1No: 'fcuData.systemAlarmSetting.reposeGroup1No',
    SystemAlarmSettingReposeGroup2No: 'fcuData.systemAlarmSetting.reposeGroup2No',
    SystemAlarmSettingDelayTimer: 'fcuData.systemAlarmSetting.delayTimer',
    SystemAlarmSettingAlarmLevel: 'fcuData.systemAlarmSetting.alarmLevel',

    StandardNameId: 'fcuData.standardNameId',
    Remarks: 'opsData.remarks',
};

export function truncateToDecimals(value: string | number, decimals: number): string {
    if (value === '' || value == null || Number.isNaN(Number(value))) return '';

    const str = String(value);
    const dotIndex = str.indexOf('.');

    if (decimals === 0) {
        return dotIndex === -1 ? str : str.slice(0, dotIndex);
    }

    if (dotIndex === -1) {
        return `${str}.${'0'.repeat(decimals)}`;
    }

    const intPart = str.slice(0, dotIndex);
    const fracPart = str.slice(dotIndex + 1);
    const adjustedFrac = fracPart.length > decimals ? fracPart.slice(0, decimals) : fracPart.padEnd(decimals, '0');
    return `${intPart}.${adjustedFrac}`;
}

const ExcelKeysMap = new Map(Object.entries(ExcelParsedProperties));
